import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { Alert, Box, CircularProgress } from '@mui/material';
import React, { useRef, useState } from 'react';
import { StyledButton } from '../../styles/StyledButtons';
import type { VideoMetadata } from '../../../services/admin/adminBackendServiceVideoLibraryMixin';
import VideoUploadTitleFields from './VideoUploadTitleFields';

const ACCEPT = 'video/mp4,video/quicktime,video/webm,video/x-matroska';

/** Upload form for the video library page: the viewer-facing title and description are mandatory. */
export default function VideoUploadForm({
  uploading,
  uploadError,
  onDismissError,
  onUpload,
}: {
  uploading: boolean;
  uploadError: string | null;
  onDismissError: () => void;
  onUpload: (metadata: VideoMetadata, file: File) => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const ready = title.trim().length > 0 && description.trim().length > 0;

  const pickFile = () => {
    if (!ready) return;
    fileInputRef.current?.click();
  };

  const handleFile = (file: File | undefined) => {
    if (!file || !ready) return;
    onUpload({ publicTitle: title.trim(), publicDescription: description.trim() }, file);
    setTitle('');
    setDescription('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <VideoUploadTitleFields
        title={title}
        onTitleChange={setTitle}
        description={description}
        onDescriptionChange={setDescription}
        disabled={uploading}
      />
      <StyledButton
        noTopSpacing
        startIcon={
          uploading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <UploadIcon />
        }
        onClick={pickFile}
        disabled={uploading || !ready}
        sx={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}
      >
        Upload
      </StyledButton>
      <input
        type="file"
        accept={ACCEPT}
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      {uploadError && (
        <Alert severity="error" onClose={onDismissError} sx={{ py: 0 }}>
          {uploadError}
        </Alert>
      )}
    </Box>
  );
}
